import { Alert, AlertTitle, Box, Button, Card, Chip, Divider, List, ListItem, styled, Typography } from '@mui/material'
import { useMutation } from '@tanstack/react-query'
import type { AxiosResponse } from 'axios'
import { Can } from 'config/ability'
import { excelExt } from 'config/extensions'
import { axiosPost } from 'connectors/axiosPost'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { Market } from 'types/Market'
import type { ImportedPrize } from '../ImportPrizes.types'
import DoneOutlinedIcon from '@mui/icons-material/DoneOutlined'
import { useAuthContext } from 'context/AuthContext'
import { useAlert } from 'context/AlertContext'
import { useNavigate } from 'react-router-dom'
import { CloudUploadOutlined } from '@mui/icons-material'
import ErrorView from 'components/ErrorView'
import { UploadFileRow } from './upload-file-row'
import { PrizeAssigned } from './prize-assigned'
import PageLoader from 'components/PageLoader'

const FileUploadBox = styled(Card)(({ theme }) => ({
  marginBottom: 8,
  borderRadius: 16,
  position: 'relative',
  display: 'flex',
  width: '100%',
  minHeight: 120,
  boxShadow: 'none',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  padding: 4,
  border: `1px solid ${theme.palette.grey[300]}`,
  '&:hover': {
    borderColor: theme.palette.grey[500],
  },

  [theme.breakpoints.up('md')]: {
    display: 'flex',
    alignItems: 'center',
  },
}))

type UploadProps = {
  market: Market
  refetch: () => void
}

export const Upload = ({ market, refetch }: UploadProps): JSX.Element => {
  const { changeMessage } = useAlert()
  const [, setApproved] = useState(false)
  const navigate = useNavigate()
  const { token, refreshToken } = useAuthContext()
  const { t } = useTranslation()
  const importInputRef = useRef<HTMLInputElement | null>(null)
  const [selectedFileToImport, setSelectedFileToImport] = useState<undefined | File>()

  const { mutate, isLoading, data, reset } = useMutation(
    async (data: FormData) =>
      await axiosPost<ImportedPrize | { error: AxiosResponse | undefined }>(
        token,
        refreshToken,
        'prize-draw/import',
        data
      ),
    {
      onSuccess: (data, ctx) => {
        if (data !== undefined && !('error' in data) && ctx.get('approved') === 'true') {
          changeMessage('Success', 'success', reset)
          navigate('/applications')
          refetch()
        }
      },
      onSettled: data => {
        if (!(data == null) && 'error' in data) {
          changeMessage(t('dialogs.sendPrizesFailure'), 'error')
        }
      },
    }
  )

  useEffect(() => {
    reset()
    setSelectedFileToImport(undefined)
  }, [market])

  const sendApproved = (): void => {
    if (selectedFileToImport !== undefined) {
      const fd = new FormData()
      if (market !== undefined) {
        fd.append('marketId', market.id.toString())
        fd.append('country', market.name)
      }
      fd.append('file', selectedFileToImport)
      fd.append('approved', 'true')
      setApproved(true)
      mutate(fd)
    }
  }

  const handleSendPrizes = (): void => {
    if (selectedFileToImport !== undefined) {
      const fd = new FormData()
      if (market !== undefined) {
        fd.append('marketId', market.id.toString())
        fd.append('country', market.name)
      }
      fd.append('file', selectedFileToImport)
      mutate(fd)
    }
  }
  if (
    data !== undefined &&
    'error' in data &&
    typeof data?.error !== 'string' &&
    data?.error !== undefined &&
    'status' in data?.error
  ) {
    if (data?.error?.status === 401) {
      return <ErrorView type="privilages" />
    }

    if (data?.error?.status === 401) {
      return <ErrorView type="account" />
    }
  }

  return (
    <Box width="100%">
      {isLoading && <PageLoader />}
      <Typography variant="h6" component="h3" fontWeight={600}>
        Database upload
      </Typography>
      <Typography variant="body2" component="p">
        Upload your .xlsx file including prize signatures here
      </Typography>
      <Divider sx={{ my: 1 }} />

      <Box>
        <Can I="upload" a="Import prizes">
          <FileUploadBox>
            <input
              ref={importInputRef}
              id="file-with-prizes"
              style={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                opacity: 0,
                cursor: 'pointer',
                zIndex: 3,
              }}
              disabled={data !== undefined && data !== null && !('error' in data)}
              accept={excelExt.join(', ')}
              onChange={e => {
                setSelectedFileToImport(e.target.files?.[0])
              }}
              type="file"
            />
            <label htmlFor="file-with-prizes">
              <Box
                style={{ pointerEvents: 'none' }}
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                gap={1}
              >
                <CloudUploadOutlined color="secondary" fontSize="large" />
                <Typography color="secondary.main" fontSize={14}>
                  <Typography component="b" color="primary" fontWeight={600} fontSize="inherit">
                    Click to upload{' '}
                  </Typography>{' '}
                  or drag and drop
                  <br />
                  XLSX or XLS file (max 20Mb)
                </Typography>
              </Box>
            </label>
          </FileUploadBox>
        </Can>
        {!(selectedFileToImport == null) && (
          <UploadFileRow
            file={selectedFileToImport}
            action={
              <Button
                onClick={handleSendPrizes}
                type="button"
                variant="contained"
                disabled={data !== undefined && data !== null && !('error' in data)}
                startIcon={<DoneOutlinedIcon />}
              >
                {t('common.next')}
              </Button>
            }
          />
        )}
      </Box>

      {data !== undefined && data !== null && !('error' in data) ? (
        <Box mt={2}>
          <Alert
            severity="warning"
            sx={{
              display: 'flex',
              border: '1px solid',
              borderColor: 'warning.main',
              alignItems: 'center',
              mb: 2,
              py: 0,
              px: 1,
            }}
            icon={<Chip title="Warning" color="warning" variant="outlined" size="small" label="Warning" />}
          >
            <AlertTitle sx={{ fontSize: '14px', lineHeight: '18px', mb: 0, color: 'warning.dark' }}>
              {market.pdRequest
                ? t('dialogs.pdRequestAfterImportPrizes')
                : t('dialogs.afterImportPrizes', {
                    recipient: t(
                      `dialogs.${market?.emailRecipient === 'all' ? 'afterImportPrizesAll' : 'afterImportPrizesWinners'}`
                    ),
                  })}
            </AlertTitle>
          </Alert>

          <List sx={{ padding: 0 }}>
            {data.map((item, idx) => (
              <ListItem key={idx}>
                <PrizeAssigned item={item} name={item.name} marketName={market.name} type="prize-draw" />
              </ListItem>
            ))}
          </List>

          {data !== undefined && data !== null && !('error' in data) ? (
            <Box display="flex" flexDirection="column" alignItems="flex-end">
              <Button onClick={sendApproved} variant="contained">
                {t('common.confirmAndSend')}
              </Button>
              <Typography variant="caption" color="grey.600" mt={0.5}>
                You can&apos;t undo this action
              </Typography>
            </Box>
          ) : null}
        </Box>
      ) : null}
    </Box>
  )
}
